/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  Block,
  Cancel,
  CheckCircleOutline,
  Pending,
  RemoveCircleOutline,
  ReportProblemOutlined,
  Schedule,
} from '@mui/icons-material';
import {CircularProgress, Stack, Tooltip, Typography} from '@mui/material';
import React from 'react';

export enum StatusTypeEnum {
  UN_PROCESSED = 'UNPROCESSED',
  INITIALISING = 'INITIALISING',
  STARTED = 'STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  FAILURE = 'FAILURE',
  PENDING = 'PENDING',
}

interface StatusIndicatorProps {
  readonly status: StatusTypeEnum;
  readonly children?: React.ReactNode;
}

const StatusIndicator = ({status, children}: StatusIndicatorProps) => {
  let icon;
  let text;
  let color;
  let toolTip;

  switch (status) {
    case StatusTypeEnum.UN_PROCESSED:
      icon = <Block />;
      text = 'Unprocessed';
      color = 'text.secondary';
      break;
    case StatusTypeEnum.INITIALISING:
      icon = <Pending />;
      text = 'Initialising';
      color = 'text.secondary';
      break;
    case StatusTypeEnum.STARTED:
      icon = <Schedule />;
      text = 'Started';
      color = 'text.secondary';
      break;
    case StatusTypeEnum.PENDING:
      icon = <Schedule />;
      text = 'Pending';
      color = 'text.secondary';
      break;
    case StatusTypeEnum.IN_PROGRESS:
      icon = <CircularProgress size={22} color={'primary'} />;
      text = 'In Progress';
      color = 'primary.main';
      break;
    case StatusTypeEnum.COMPLETED:
    case StatusTypeEnum.SUCCESS:
      icon = <CheckCircleOutline color={'success'} />;
      text = 'Completed';
      color = 'success.main';
      break;
    case StatusTypeEnum.FAILED:
    case StatusTypeEnum.FAILURE:
      icon = <Cancel color={'error'} />;
      text = 'Failed';
      color = 'error.main';
      break;
    case StatusTypeEnum.PARTIAL_SUCCESS:
      icon = <ReportProblemOutlined color={'warning'} />;
      text = 'Post Import Processing Failed';
      color = 'warning.main';
      toolTip = 'Analytics results may be affected by this error';
      break;
    default:
      icon = <RemoveCircleOutline />;
      text = 'Unknown';
      color = 'text.secondary';
  }

  const statusElements = (
    <Stack direction={'row'} spacing={1} alignItems={'center'} sx={{color}}>
      {icon}
      <Typography>{children ?? text}</Typography>
    </Stack>
  );

  if (toolTip) {
    return (
      <Tooltip title={toolTip} placement={'top'} arrow>
        {statusElements}
      </Tooltip>
    );
  }

  return statusElements;
};

export default StatusIndicator;
